/**
 * LAS LETRAS QUE TIENE GOOGLE DOCS, en el editor (16 oct 2026). Lo mismo que
 * hace el backend al exportar (processing-ms/src/common/letras-docs.ts): una
 * letra que Docs no tiene la pinta con otra de reserva y otra línea natural, así
 * que el editor muestra la que Docs sí tiene y se ve igual:
 *   - Helvetica → Arial (mismas métricas y anchos);
 *   - las CJK de sistema (Malgun Gothic, Batang, MS Mincho, SimSun, PMingLiU…)
 *     → Noto Sans KR / JP / SC / TC, con los mismos archivos que processing-ms y
 *     OnlyOffice (public/fonts/cjk, con la línea natural de Docs).
 * Se aplica al leer el HTML (filtro del parser): lo que se guarda ya va así.
 */
const CJK: Array<[RegExp, string]> = [
  [/^(malgun gothic|맑은 고딕|gulim|굴림|gulimche|dotum|돋움|dotumche|batang|바탕|batangche|gungsuh|궁서|apple sd gothic neo|applegothic|nanum ?gothic|nanum ?myeongjo|nanumbarungothic|noto sans cjk kr|noto serif cjk kr|source han sans k|source han serif k|noto serif kr|undotum|unbatang)$/, 'Noto Sans KR'],
  [/^(ms gothic|ms pgothic|ms ui gothic|ms mincho|ms pmincho|meiryo|meiryo ui|yu gothic|yu gothic ui|yu mincho|hiragino.*|osaka|ipagothic|ipamincho|ipaexgothic|ipaexmincho|takao.*|noto sans cjk jp|noto serif cjk jp|source han sans j.*|noto serif jp|ms 明朝|ms ゴシック|游ゴシック|游明朝|メイリオ)$/, 'Noto Sans JP'],
  [/^(simsun|nsimsun|simhei|microsoft yahei|microsoft yahei ui|dengxian|kaiti|fangsong|stsong|stheiti|stkaiti|pingfang sc|heiti sc|songti sc|noto sans cjk sc|noto serif cjk sc|source han sans (sc|cn)|noto serif sc|宋体|黑体|微软雅黑)$/, 'Noto Sans SC'],
  [/^(pmingliu|mingliu|mingliu_hkscs|microsoft jhenghei|microsoft jhenghei ui|pingfang tc|pingfang hk|heiti tc|noto sans cjk tc|noto sans cjk hk|noto serif cjk tc|source han sans (tc|hk|tw)|noto serif tc|新細明體|細明體|微軟正黑體)$/, 'Noto Sans TC'],
];

const limpia = (f: string) => f.replace(/["']/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

const GENERICAS = /^(serif|sans-serif|monospace|cursive|fantasy|system-ui)$/;
const CJK_SERIF = /batang|gungsuh|myeongjo|mincho|simsun|songti|stsong|mingliu|kaiti|stkaiti|fangsong|serif|宋体|明朝|細明體/;

/**
 * Una letra CJK de sistema: el latín en la letra latina de la pila (la primera
 * que no sea CJK ni genérica), o en la que pide su genérica (serif → Times New
 * Roman, monospace → Courier New; si no, Arial), o según la letra (las de
 * tipo «Batang» o «Mincho», Times New Roman). Es lo que pintaban el editor, el
 * PDF y OnlyOffice antes con una letra que no tienen. El CJK, en la Noto.
 */
function pilaCjk(familias: string[], primera: string, noto: string): string {
  const resto = familias.slice(1);
  const generica = resto.map(limpia).find((f) => GENERICAS.test(f));
  const latina = resto.find((f) => {
    const l = limpia(f);
    return !GENERICAS.test(l) && !CJK.some(([re]) => re.test(l)) && !/^noto sans (kr|jp|sc|tc)$/.test(l);
  });
  const serif = generica ? generica === 'serif' : CJK_SERIF.test(primera);
  const porTipo = generica === 'monospace' ? "'Courier New'" : serif ? "'Times New Roman'" : 'Arial';
  const nombre = (f: string) => {
    const n = f.replace(/&(?:apos|quot|#39|#34);/g, '').replace(/["']/g, '').trim();
    if (/^helvetica( neue)?$/i.test(n)) return 'Arial';
    return /\s/.test(n) ? `'${n}'` : n;
  };
  return `${latina ? nombre(latina) : porTipo}, '${noto}', ${generica ?? (serif ? 'serif' : 'sans-serif')}`;
}

/** La pila que Docs pinta igual que el editor; la misma si no hay nada que cambiar. */
export function pilaParaDocs(pila: string): string {
  const familias = pila.split(',').map((f) => f.trim()).filter(Boolean);
  if (!familias.length) return pila;
  const primera = limpia(familias[0]);
  if (/^helvetica( neue)?$/.test(primera)) return ['Arial', ...familias.slice(1).filter((f) => !/^["']?arial["']?$/i.test(f))].join(', ');
  for (const [re, noto] of CJK) if (re.test(primera)) return pilaCjk(familias, primera, noto);
  // La pila que escribía esta misma función entre el 16 y el 17 oct («'Noto Sans KR', sans-serif»): el latín, a Arial.
  if (familias.length === 2 && /^noto sans (kr|jp|sc|tc)$/.test(primera) && /^sans-serif$/i.test(familias[1])) return `Arial, ${familias[0]}, sans-serif`;
  return pila;
}

/** Un style="" con su font-family en letras de Docs. */
export function estiloConLetrasDeDocs(estilo: string): string {
  return estilo.replace(/(font-family\s*:\s*)([^;]+)/gi, (m, pre: string, pila: string) => {
    const nueva = pilaParaDocs(pila.trim());
    return nueva === pila.trim() ? m : `${pre}${nueva}`;
  });
}

/** Las Noto CJK del editor: los mismos archivos que processing-ms y OnlyOffice (solo se descargan si hay texto CJK). */
export const NOTO_CJK = ['KR', 'JP', 'SC'] as const;
const RANGO_CJK = 'U+1100-11FF, U+2E80-2FDF, U+3000-30FF, U+3130-318F, U+3190-31FF, U+3400-4DBF, U+4E00-9FFF, U+A960-A97F, U+AC00-D7AF, U+D7B0-D7FF, U+F900-FAFF, U+FE30-FE4F, U+FF00-FFEF';

export function cssNotoCjk(): string {
  const caras = NOTO_CJK.flatMap((l) => (['Regular', 'Bold'] as const).map((w) =>
    `@font-face { font-family: 'Noto Sans ${l}'; src: url('/fonts/cjk/NotoSans${l}-${w}.otf') format('opentype'); font-weight: ${w === 'Bold' ? 'bold' : 'normal'}; font-style: normal; font-display: swap; }`));
  // TC: Docs la tiene; aquí, la SC (mismos trazos para lo que comparten y la misma línea natural).
  caras.push(...(['Regular', 'Bold'] as const).map((w) =>
    `@font-face { font-family: 'Noto Sans TC'; src: url('/fonts/cjk/NotoSansSC-${w}.otf') format('opentype'); font-weight: ${w === 'Bold' ? 'bold' : 'normal'}; font-style: normal; font-display: swap; unicode-range: ${RANGO_CJK}; }`));
  return caras.join('\n');
}

type EditorLetras = {
  on: (evento: string, fn: () => void) => void;
  parser?: { addAttributeFilter: (nombre: string, fn: (nodos: Array<{ attr: (n: string, v?: string) => string | undefined }>) => void) => void };
};

/** Engancha el cambio de letra al parser del editor (en `setup`). Si algo falla, la letra se queda como venía. */
export function registrarLetrasDeDocs(ed: EditorLetras): void {
  ed.on('PreInit', () => {
    try {
      ed.parser?.addAttributeFilter('style', (nodos) => {
        for (const nodo of nodos) {
          const estilo = nodo.attr('style');
          if (!estilo || !/font-family/i.test(estilo)) continue;
          const nuevo = estiloConLetrasDeDocs(estilo);
          if (nuevo !== estilo) nodo.attr('style', nuevo);
        }
      });
    } catch { /* la letra se queda como venía */ }
  });
}
